<template>
  <span class="input-wrapper" ref="wrapper" :style="_wrapperStyle">
    <slot name="prefix"></slot>
    <input
      ref="input"
      class="input"
      :style="_innerStyle"
      :maxlength="props.maxlength"
      :type="props.type ?? 'text'"
      :disabled="props.disabled"
      :placeholder="props.placeholder"
      :oninput="handleInput"
      @change="handleChange"
      @clear="handleClear"
    />
    <div
      v-if="props.allowClear && props.modelValue && !props.disabled"
      class="clear iconfont icon-guanbiicon"
      @mousedown.prevent="handleClear"
    ></div>
    <slot name="suffix"></slot>
  </span>
</template>

<script setup lang="ts">
import {
  defineProps,
  defineEmits,
  shallowRef,
  computed,
  watch,
  nextTick,
  onMounted,
} from "vue";
import { inputProps, styleProps, inputEmit } from "./input";

type TargetElement = HTMLInputElement | HTMLTextAreaElement;

const props = defineProps(inputProps);
const emits = defineEmits(inputEmit);

const input = shallowRef<HTMLInputElement>();
const wrapper = shallowRef<HTMLDivElement>();
const _inputRef = computed(() => input.value);
const _wrapperRef = computed(() => wrapper.value);

// 处理输入的值
const nativeInputValue = computed(() => String(props.modelValue));
watch(nativeInputValue, () => setNativeInputValue());
const setNativeInputValue = () => {
  const input = _inputRef.value;
  if (!input || input.value === nativeInputValue.value) return;
  input.value = nativeInputValue.value;
};

// 处理外部的style
const _wrapperStyle = computed(() => {
  let style = {};
  if (props.height) {
    style = Object.assign(style, { height: props.height + "px" });
  }
  if (props.width) {
    style = Object.assign(style, { width: props.width + "px" });
  }
  if (!props.border) {
    style = Object.assign(style, { border: "none" });
  }
  if (props.backgroundColor) {
    style = Object.assign(style, { backgroundColor: props.backgroundColor });
  }
  style = Object.assign(style, props.inputWrapperStyle);
  return style;
});
// 处理内部样式
const _innerStyle = computed(() => {
  let style = {};

  if (props.placeholderPosition === "right") {
    style = Object.assign(style, {
      "--placeholderPosition": props.placeholderPosition,
      "text-align": props.placeholderPosition,
    });
  }
  if (props.backgroundColor) {
    style = Object.assign(style, { backgroundColor: props.backgroundColor });
  }
  style = Object.assign(style, props.inputInnterStyle);
  return style;
});

const handleInput = async (event: Event) => {
  let { value } = event.target as TargetElement;
  emits("update:modelValue", value);
  emits("input", value);
  await nextTick();
  setNativeInputValue();
};

const handleChange = (event: Event) => {
  let { value } = event.target as TargetElement;
  emits("change", value);
};

const handleClear = () => {
  emits("update:modelValue", "");
  emits("change", "");
  emits("clear");
  setNativeInputValue();
};

onMounted(() => {
  setNativeInputValue();
});
</script>

<style lang="less" scoped>
.input-wrapper {
  border: 1px solid rgba(238, 238, 238, 1);
  display: inline-flex;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding-right: 12px;
  padding-left: 12px;
  font-size: 14px;
  cursor: text;
  transition: color 0.1s cubic-bezier(0, 0, 1, 1),
    border-color 0.1s cubic-bezier(0, 0, 1, 1),
    background-color 0.1s cubic-bezier(0, 0, 1, 1);
}

.input-wrapper:focus-within {
  background-color: #fff;
  border-color: rgb#A9823E;
  box-shadow: 0 0 0 0 rgb(190, 218, 255);
}

.input {
  height: 100%;
  width: 100%;
  border: none;
  font-size: 14px;
  color: #1a1a1a;
}
.input:focus {
  outline: none;
}
.input::placeholder {
  text-align: var(--placeholderPosition);
}

.clear {
  margin-left: 1px;
  align-self: center;
  cursor: pointer;
  margin-top: 2px;
}
</style>
